// src/data/learnContent.js
// Learn section content — 18 articles across 6 topics
// Seeker-facing educational content at /learn

export const LEARN_TOPICS = [
  {
    id: 'starting-with-an-open-mind',
    slug: 'starting-with-an-open-mind',
    title: 'New Here? Start Here',
    subtitle: "Whether you're exploring for the first time or just curious, start here.",
    icon: '\u{1F331}',
    color: 'gold',
    articleCount: 3,
    sections: [
      {
        id: 'curious-but-skeptical',
        title: "I'm Curious but Skeptical — Is That OK?",
        slug: 'curious-but-skeptical',
        videoUrl: null,
        videoPlaceholder: true,
        readTime: 3,
        keywords: ['skeptical', 'skepticism', 'doubt', 'curious', 'real', 'believe', 'trust', 'first time'],
        searchMetadata: {
          aiKeywords: ['skeptical', 'doubt', 'curious', 'believe', 'trust'],
          intentPhrases: ['is it ok to be skeptical', 'not sure if I believe', 'curious but doubtful'],
          tags: ['beginner', 'mindset'],
        },
        content: `<p>Yes. Full stop.</p>

<p>If you're reading this with one eyebrow raised, you're in good company. A lot of people who end up having deeply meaningful experiences with spiritual and wellness practitioners started exactly where you are — interested enough to look, but not sure what to expect.</p>

<p>It's worth saying upfront: not every practice on this platform asks you to take the same leap. Booking a life coaching session or a yoga class doesn't raise the same kinds of questions as booking a medium or an energy healer. If you're drawn to the more conventional wellness side, you may not need this article at all — and that's great. But if you're exploring the less familiar side of things, keep reading.</p>

<p>Here's something that might surprise you: most practitioners <em>prefer</em> working with people who ask questions. Healthy skepticism means you're thinking critically, not just accepting everything at face value. That's a sign of intelligence, not a barrier to the experience.</p>

<p>What doesn't serve you is walking in with your arms crossed, determined to prove something wrong. That's not skepticism — that's a wall. And it usually means the person isn't ready, which is also completely fine.</p>

<p>The invitation isn't to believe everything. It's to stay open to what you might experience. There's a difference between "I don't think this is real" and "I'm not sure what to expect, but I'm willing to find out." One closes doors. The other opens them.</p>

<p>You don't need to be a believer to benefit from a session. You just need to be willing to show up honestly.</p>`
      },
      {
        id: 'what-is-a-spiritual-practice',
        title: 'What Are Spiritual & Wellness Practices?',
        slug: 'what-is-a-spiritual-practice',
        videoUrl: null,
        readTime: 4,
        keywords: ['spiritual practice', 'what is', 'definition', 'types', 'wellness', 'holistic', 'meaning', 'explain', 'yoga', 'coaching'],
        searchMetadata: {
          aiKeywords: ['spiritual practice', 'wellness', 'holistic', 'types of practices', 'yoga', 'coaching'],
          intentPhrases: ['what is a spiritual practice', 'types of spiritual practices', 'explain spiritual wellness'],
          tags: ['beginner', 'overview'],
        },
        content: `<p>The phrases "spiritual practice" and "wellness practice" can cover a lot of ground — from a guided meditation to a tarot reading to a private yoga session to a life coaching call. The common thread isn't a belief system. It's an intention: supporting your physical, mental, emotional, or spiritual wellbeing in a way that goes deeper than a quick fix.</p>

<p>For some people, that looks like sitting with an astrologer. For others, it's a breathwork class, a nutrition consultation, or a coaching session focused on a career change. None of these is more "valid" than another. They're different tools for different needs.</p>

<p>The world of spiritual and wellness practices is broad. Here's a quick map:</p>

<p><strong>Wellness & Coaching</strong> — life coaching, wellness coaching, yoga instruction, nutrition counseling, mindfulness training, somatic work. These are practical, often goal-oriented, and focused on your whole-person health. Many people start here.</p>

<p><strong>Body-Based Practices</strong> — breathwork, yoga, sound healing, movement therapy. These work with your physical body to promote relaxation, release, and awareness. They range from gentle to intense.</p>

<p><strong>Energy Work</strong> — Reiki, chakra balancing, acupuncture, qigong. These work with the body's energy systems. You might feel warmth, tingling, or deep calm — or you might feel very little in the moment but notice shifts over the following days.</p>

<p><strong>Divination & Insight</strong> — tarot, oracle cards, astrology, numerology. These are tools for reflection and pattern recognition, not fortune-telling. A good reader helps you see possibilities, not predict a fixed future.</p>

<p><strong>Intuitive & Mediumship</strong> — connecting with intuition, spirit guides, or loved ones who have passed. This is deeply personal work that requires trust and openness.</p>

<p>You don't need to understand all of it. You don't need to pick a lane. You just need to know that whatever drew you here — curiosity, a recommendation, a feeling you can't quite name — is a perfectly valid starting point.</p>`
      },
      {
        id: 'not-about-religion-or-politics',
        title: "This Isn't About Religion or Politics",
        slug: 'not-about-religion-or-politics',
        videoUrl: null,
        readTime: 3,
        keywords: ['religion', 'politics', 'beliefs', 'inclusive', 'non-religious', 'secular', 'faith', 'judgment'],
        searchMetadata: {
          aiKeywords: ['religion', 'politics', 'beliefs', 'inclusive', 'secular'],
          intentPhrases: ['is this religious', 'do I need to believe', 'is this political'],
          tags: ['beginner', 'mindset'],
        },
        content: `<p>One of the most common hesitations people have is the worry that spiritual and wellness practices come with a belief system attached — that you'll be asked to convert, to accept a doctrine, or to align with a particular worldview.</p>

<p>That's not what this is.</p>

<p>The practitioners on Divine Frameworks come from all kinds of backgrounds and traditions. Some draw from ancient spiritual lineages, some from modern wellness science, and many from a blend of both. You'll find tarot readers alongside yoga teachers, energy healers alongside life coaches. What they share isn't a single belief system — it's a commitment to helping people.</p>

<p>You can be religious and explore these practices. You can be atheist and explore these practices. You can be somewhere in between and still have a meaningful experience. No one is going to ask you what you believe or tell you what you should believe.</p>

<p>Similarly, this space isn't political. It's personal. It's about your inner world — your growth, your healing, your questions — not anyone's agenda.</p>

<p>What we do ask is mutual respect. The practitioners here have spent years developing their skills and their understanding — whether that's through intuitive development, yoga teacher training, coaching certification, or decades of personal practice. You don't have to agree with everything, but approaching the experience with basic respect goes a long way.</p>

<p>This is a space for everyone. That only works when everyone treats it that way.</p>`
      },
    ],
  },
  {
    id: 'what-to-expect',
    slug: 'what-to-expect',
    title: 'What to Expect',
    subtitle: 'Everything you want to know before your first session.',
    icon: '\u{1F9ED}',
    color: 'gold',
    articleCount: 4,
    sections: [
      {
        id: 'your-first-session',
        title: 'Your First Session: What Actually Happens',
        slug: 'your-first-session',
        videoUrl: null,
        videoPlaceholder: true,
        readTime: 4,
        keywords: ['first session', 'what happens', 'expect', 'prepare', 'before', 'during', 'after', 'nervous'],
        searchMetadata: {
          aiKeywords: ['first session', 'what to expect', 'prepare', 'before session'],
          intentPhrases: ['what happens in a session', 'how to prepare for first session', 'what to expect'],
          tags: ['preparation', 'first-time'],
        },
        content: `<p>The unknown is usually scarier than the reality. Here's what a typical first session looks like — though keep in mind that every practitioner and every practice is a little different. Your first session might be a tarot reading, a yoga class, a coaching call, a Reiki session, or a breathwork workshop. The general flow is more similar than you'd think.</p>

<h3>Before the session</h3>

<p>Most practitioners will share some preparation guidance — it might be as simple as "come with an open mind" or it might include specific suggestions like journaling questions you want to explore, wearing comfortable clothes, eating lightly beforehand, or setting an intention. If they send you an intake form, fill it out honestly. It helps them prepare for <em>you</em> specifically, not give a generic experience.</p>

<h3>The beginning</h3>

<p>Sessions usually start with a brief conversation. A coach might ask about your goals. A yoga teacher might ask about injuries. A tarot reader might ask what questions you're holding. This isn't a test — it's so they can focus their attention on what matters most to you.</p>

<h3>During the session</h3>

<p>This is where it varies. A tarot reading might involve cards being drawn and interpreted. A coaching session might be a deep conversation with exercises and action steps. A yoga class might guide you through movement and breath. An energy healing might have you lying down while the practitioner works quietly. Whatever the format, you're welcome to ask questions, share reactions, or simply receive.</p>

<h3>After the session</h3>

<p>You might feel energized, emotional, peaceful, focused, or a bit stirred up — all normal responses. Many practitioners will suggest you drink water, take it easy, and give yourself some time to process. Some sessions click immediately. Others take days to fully land. Both are valid.</p>

<p>The most important thing? There's no wrong way to experience it.</p>`
      },
      {
        id: 'ok-to-be-nervous',
        title: "It's OK to Be Nervous — Most People Are",
        slug: 'ok-to-be-nervous',
        videoUrl: null,
        readTime: 3,
        keywords: ['nervous', 'anxiety', 'scared', 'worried', 'fear', 'normal', 'first time', 'feelings'],
        searchMetadata: {
          aiKeywords: ['nervous', 'anxiety', 'scared', 'worried', 'normal'],
          intentPhrases: ['is it normal to be nervous', 'scared of first session', 'what if I cry'],
          tags: ['emotions', 'first-time'],
        },
        content: `<p>If your palms are a little sweaty thinking about booking your first session, that's completely normal. You're stepping into unfamiliar territory, and your brain is doing what brains do — trying to protect you from the unknown.</p>

<p>Here's what might be running through your head:</p>

<p><em>"What if they tell me something bad?"</em> — A good practitioner isn't there to scare you. They're there to help you. If something sensitive comes up, they'll handle it with care and context.</p>

<p><em>"What if I cry?"</em> — Practitioners see this regularly. It's not embarrassing to them — it's often a sign that something real is happening. Bring tissues if it makes you feel better. No one is judging.</p>

<p><em>"What if nothing happens?"</em> — That's OK too. Not every session is a fireworks display. Sometimes the value shows up later — in a dream, a decision you make differently, or a shift you notice over the following days.</p>

<p><em>"What if I'm not flexible enough?"</em> — If you're trying yoga or a body-based practice, this is the most common worry — and the least warranted. Good teachers meet you where you are.</p>

<p><em>"What if I don't know what to talk about?"</em> — If you're booking a coaching session or a reading, you don't need a script. The practitioner will guide the conversation. Just show up as you are.</p>

<p>Nervousness often comes from caring about the experience — wanting it to be meaningful, wanting to do it right. That's actually a beautiful place to start from.</p>

<p>Take a breath. Book when you're ready. And know that the person on the other end of that session has sat with hundreds of nervous first-timers before you.</p>`
      },
      {
        id: 'how-to-show-up-respectfully',
        title: 'How to Show Up Respectfully',
        slug: 'how-to-show-up-respectfully',
        videoUrl: null,
        readTime: 3,
        keywords: ['respect', 'respectful', 'etiquette', 'behavior', 'how to act', 'manners', 'protocol'],
        searchMetadata: {
          aiKeywords: ['respect', 'etiquette', 'behavior', 'how to act'],
          intentPhrases: ['how to be a good client', 'session etiquette', 'how to show up'],
          tags: ['preparation', 'etiquette'],
        },
        content: `<p>You don't need special training to be a good client. Most of this is the same basic respect you'd bring to any professional interaction — with a few things worth keeping in mind.</p>

<p><strong>Be on time.</strong> Whether it's a virtual coaching call, an in-person yoga class, or an energy healing session, practitioners prepare before you arrive. Showing up late doesn't just cut into your time — it disrupts theirs.</p>

<p><strong>Be honest.</strong> If you're skeptical, say so. If you're nervous, say so. If you don't understand something, ask. Practitioners would much rather work with someone who's honest about where they are than someone performing openness they don't feel.</p>

<p><strong>Don't test them.</strong> This applies especially to intuitive and spiritual practitioners: walking in with a hidden "quiz" — like withholding information to see if they can "guess" it — puts up a wall that makes the session harder for both of you. This isn't a performance. It's a collaboration.</p>

<p><strong>Respect their expertise.</strong> Whether someone is a certified yoga teacher, a trained life coach, or an intuitive reader with decades of experience, they've invested real time developing their skills. You wouldn't tell your dentist how to hold the drill. The same principle applies here.</p>

<p><strong>Honor their time and policies.</strong> Cancellation policies exist because practitioners often turn away other clients to hold your spot. If you need to cancel, do it within the policy window.</p>

<p><strong>Keep an open mind.</strong> You might hear something that doesn't make sense in the moment but clicks later. You might be guided into a stretch you didn't think you could do. You might experience something you can't explain. Let it be what it is without needing to immediately categorize or dismiss it.</p>

<p>The simplest version of all of this: treat the practitioner like a professional, and treat the experience like it matters. Because it does — to both of you.</p>`
      },
      {
        id: 'not-every-session-lightning-bolt',
        title: 'Not Every Session Will Be a Lightning Bolt',
        slug: 'not-every-session-lightning-bolt',
        videoUrl: null,
        readTime: 3,
        keywords: ['expectations', 'disappointed', 'subtle', 'nothing happened', 'underwhelming', 'realistic', 'patience'],
        searchMetadata: {
          aiKeywords: ['expectations', 'disappointed', 'subtle', 'realistic'],
          intentPhrases: ['nothing happened in my session', 'was my session supposed to be more intense', 'managing expectations'],
          tags: ['expectations', 'patience'],
        },
        content: `<p>Social media is full of dramatic testimonials: "She told me the exact name of my grandmother!" "I felt energy shooting through my entire body!" "It completely changed my life in one session!"</p>

<p>Those experiences are real. They also aren't the norm for everyone, every time.</p>

<p>The truth is, sessions exist on a spectrum — whether we're talking about a tarot reading, a coaching call, a yoga class, or a healing session. Sometimes you'll walk out feeling like the entire universe just rearranged itself for you. Other times, you'll walk out thinking, "That was nice, but I'm not sure what just happened." Both are valid outcomes.</p>

<p>A coaching session might not produce an immediate breakthrough, but the question it plants might change how you approach a decision next week. A yoga class might feel unremarkable, but your sleep that night might be the best you've had in months. A reading might not make sense until weeks later when a situation unfolds exactly as described.</p>

<p>Here's what practitioners want you to know about expectations:</p>

<p><strong>Subtle doesn't mean unsuccessful.</strong> A session that plants a quiet seed — one that grows into a realization days or weeks later — is just as valuable as one that hits you like a tidal wave.</p>

<p><strong>Not every practitioner is your match.</strong> If a session doesn't resonate, it doesn't mean the practice itself is flawed. It might mean that particular practitioner's style, approach, or energy isn't the right fit for you. That's normal and OK.</p>

<p><strong>Your state of mind matters.</strong> If you come in stressed, guarded, or distracted, the experience will feel different than if you come in present and grounded. This isn't blame — it's just how it works. Even practitioners have off days.</p>

<p><strong>Depth comes with time.</strong> Many people find that their experiences deepen over multiple sessions as trust builds and they learn how to receive. The first session is often about establishing the relationship, not achieving a peak experience.</p>

<p>Give yourself permission to have whatever experience you have — without measuring it against someone else's Instagram post.</p>`
      },
    ],
  },
  {
    id: 'finding-the-right-fit',
    slug: 'finding-the-right-fit',
    title: 'Finding the Right Fit',
    subtitle: "Not every practitioner is your practitioner — and that's a good thing.",
    icon: '\u{1F91D}',
    color: 'gold',
    articleCount: 3,
    sections: [
      {
        id: 'not-every-practitioner',
        title: "Not Every Practitioner Is Your Practitioner",
        slug: 'not-every-practitioner',
        videoUrl: null,
        readTime: 3,
        keywords: ['choose', 'find', 'right practitioner', 'fit', 'match', 'chemistry', 'try different', 'not right'],
        searchMetadata: {
          aiKeywords: ['choose practitioner', 'right fit', 'match', 'chemistry'],
          intentPhrases: ['how to find the right practitioner', 'my session did not resonate', 'try someone different'],
          tags: ['choosing', 'fit'],
        },
        content: `<p>This might be the most important thing to understand before you start: the practitioner matters as much as the practice.</p>

<p>Two tarot readers can use the same deck and have completely different styles. Two yoga teachers can teach the same poses with completely different energy. Two coaches can ask similar questions and create entirely different experiences. One might be gentle and nurturing. The other might be direct and no-nonsense. Neither is better — but one might be a much better fit for <em>you</em>.</p>

<p>If you have a session that doesn't resonate, resist the urge to write off the entire practice. Instead, consider whether it was the practice you didn't connect with, or the person delivering it.</p>

<p>Things that vary between practitioners:</p>

<p><strong>Communication style.</strong> Some are warm and conversational. Others are quiet and let the work speak. Some explain everything they're doing. Others work more intuitively and share at the end.</p>

<p><strong>Approach and philosophy.</strong> Some practitioners are deeply spiritual in their framework. Others are more practical and evidence-based. Some blend both. What resonates with you is personal.</p>

<p><strong>Energy and presence.</strong> This one is harder to articulate, but you'll feel it. Some people just feel right to work with. Trust that feeling — whether you're choosing a healer, a coach, or a teacher.</p>

<p>It's completely acceptable — and actually encouraged — to try sessions with different practitioners until you find someone who clicks. Most practitioners understand this and won't take it personally.</p>

<p>Think of it like finding a therapist, a doctor, or even a hairstylist. The credentials matter, but so does the relationship. Give yourself permission to shop around.</p>`
      },
      {
        id: 'understanding-different-practices',
        title: 'Understanding Different Practices',
        slug: 'understanding-different-practices',
        videoUrl: null,
        videoPlaceholder: true,
        readTime: 5,
        keywords: ['tarot', 'mediumship', 'energy healing', 'reiki', 'astrology', 'coaching', 'yoga', 'types', 'differences', 'which one'],
        searchMetadata: {
          aiKeywords: ['tarot', 'reiki', 'mediumship', 'astrology', 'coaching', 'energy healing', 'yoga'],
          intentPhrases: ['what is tarot', 'difference between practices', 'which practice should I try'],
          tags: ['overview', 'practices'],
        },
        content: `<p>The world of spiritual and wellness services is wide, and the labels can be confusing. Here's a plain-language guide to the most common practices you'll find on Divine Frameworks.</p>

<p><strong>Life Coaching & Wellness Coaching</strong></p>
<p>Practical, goal-oriented guidance for your personal or professional life. Coaches help you get clarity, build action plans, and stay accountable. Some incorporate spiritual or holistic perspectives, while others are purely pragmatic. Either way, the focus is on you moving forward.</p>

<p><strong>Yoga & Movement</strong></p>
<p>Far more than exercise, yoga combines movement, breath, and mindfulness into a practice that supports both body and mind. Styles range from physically demanding (vinyasa, power yoga) to gentle and restorative (yin, restorative). A good teacher will meet you wherever you are physically.</p>

<p><strong>Breathwork</strong></p>
<p>Guided breathing techniques that can produce altered states, emotional release, physical sensations, and deep relaxation. This is more physically intense than most people expect — and more transformative too.</p>

<p><strong>Nutrition & Holistic Wellness</strong></p>
<p>Whole-person approaches to health that consider diet, lifestyle, stress, and environment together. This might look like nutrition counseling, Ayurvedic consultation, or integrative wellness coaching.</p>

<p><strong>Energy Healing (Reiki, Chakra Work, Sound Healing)</strong></p>
<p>These practices work with the body's energy systems to promote balance, relaxation, and release. You might feel warmth, tingling, emotional shifts, or deep calm — or you might feel very little in the moment but notice changes over the following days.</p>

<p><strong>Tarot & Oracle Card Reading</strong></p>
<p>A practitioner uses a deck of cards as a tool to explore your questions, patterns, and potential paths forward. It's not fortune-telling — it's a framework for reflection and insight. Think of it as a structured conversation with your subconscious.</p>

<p><strong>Astrology</strong></p>
<p>Based on the positions of planets and stars at the time of your birth, an astrologer interprets patterns that may relate to your personality, life themes, timing, and potential. A natal chart reading is the most common starting point.</p>

<p><strong>Mediumship</strong></p>
<p>A medium connects with the energy or consciousness of people who have passed. This is deeply personal work and requires a level of trust and openness. Not every session will produce specific messages — and that's OK.</p>

<p><strong>Intuitive Reading</strong></p>
<p>A practitioner uses their intuition to offer insights about your life, relationships, or path. This can overlap with many of the above categories.</p>

<p>Don't worry about understanding all of these before booking. Read the practitioner's description of their services — they'll explain their specific approach in their own words.</p>`
      },
      {
        id: 'how-to-choose-a-practitioner',
        title: 'How to Choose a Practitioner',
        slug: 'how-to-choose-a-practitioner',
        videoUrl: null,
        readTime: 4,
        keywords: ['choose', 'pick', 'select', 'find', 'reviews', 'ratings', 'bio', 'qualifications', 'trust'],
        searchMetadata: {
          aiKeywords: ['choose', 'pick', 'reviews', 'trust', 'qualifications'],
          intentPhrases: ['how to choose a practitioner', 'what to look for in a practitioner', 'are reviews trustworthy'],
          tags: ['choosing', 'practical'],
        },
        content: `<p>With so many practitioners and practices available, how do you pick the right one? Here are some practical things to consider.</p>

<p><strong>Read their bio and about section.</strong> Do they sound like someone you'd feel comfortable talking to? Does their philosophy resonate? Do they explain what they do in a way that makes sense to you? If their bio feels aligned with how you see the world, that's a good sign.</p>

<p><strong>Look at their services.</strong> What do they offer? How long are the sessions? What's included? A practitioner who clearly describes their services — whether it's a 60-minute coaching call, a yoga private, or a card reading — is usually one who takes their work seriously.</p>

<p><strong>Check reviews.</strong> Other people's experiences can give you a sense of what to expect. Look for patterns — if multiple people mention feeling comfortable, heard, or surprised by the quality, that tells you something. One negative review in a sea of positive ones is normal. A pattern of complaints is a flag.</p>

<p><strong>Trust your instinct.</strong> Your own gut feeling matters. If something about a practitioner's profile draws you in — or puts you off — pay attention to that.</p>

<p><strong>Start with what you need.</strong> Are you going through a transition? Look for someone who specializes in that. Want to build a meditation habit? Find a mindfulness teacher. Grieving? A medium or grief-focused healer. Feeling stuck in your career? A coach. The clearer you are about what you're looking for, the easier it is to find the right match.</p>

<p><strong>Price isn't everything.</strong> A higher price doesn't guarantee a better experience, and a lower price doesn't mean lower quality. Many newer practitioners offer excellent work at accessible prices. What matters is the connection.</p>

<p>And remember — your first choice doesn't have to be your only choice. Finding the right practitioner is a process, and that's perfectly normal.</p>`
      },
    ],
  },
  {
    id: 'going-deeper',
    slug: 'going-deeper',
    title: 'Going Deeper',
    subtitle: 'Ready to explore beyond your first session.',
    icon: '\u{1F30A}',
    color: 'gold',
    articleCount: 3,
    sections: [
      {
        id: 'building-your-own-practice',
        title: 'Building Your Own Practice Between Sessions',
        slug: 'building-your-own-practice',
        videoUrl: null,
        readTime: 3,
        keywords: ['personal practice', 'journaling', 'meditation', 'between sessions', 'self-care', 'routine', 'daily practice'],
        searchMetadata: {
          aiKeywords: ['personal practice', 'journaling', 'meditation', 'self-care', 'routine'],
          intentPhrases: ['what to do between sessions', 'how to build a personal practice', 'daily spiritual routine'],
          tags: ['growth', 'practice'],
        },
        content: `<p>Sessions with a practitioner are powerful, but they're moments in time. What you do between sessions is what builds lasting change — whether your practice is spiritual, physical, or somewhere in between.</p>

<p>You don't need an elaborate daily ritual (unless that appeals to you). Here are simple ways to keep the momentum going:</p>

<p><strong>Journal after every session.</strong> Write down what came up, what surprised you, what resonated, and what you're still processing. This works whether you just had a tarot reading, a coaching call, or a yoga class. You'd be amazed how much you forget within 48 hours — and how valuable those notes become months later when patterns emerge.</p>

<p><strong>Sit quietly for five minutes a day.</strong> Call it meditation if you want. Call it "sitting and breathing" if that feels less intimidating. The point is giving your mind a few minutes of quiet to process, integrate, and rest.</p>

<p><strong>Move your body.</strong> If your practice is more mind-focused (readings, coaching), counterbalance with something physical — a walk, a stretch, a class. If your practice is body-focused (yoga, breathwork), counterbalance with reflection or journaling. The integration of both is where real change happens.</p>

<p><strong>Follow through on anything specific.</strong> If your practitioner suggested something — a book, a practice, a conversation you need to have, a pose to work on at home — follow through. The session opens the door, but you have to walk through it.</p>

<p><strong>Be gentle with yourself.</strong> Growth isn't linear. Some weeks you'll feel expanded and clear. Other weeks you'll feel like you've gone backwards. Both are part of the process.</p>

<p>You don't need to become a spiritual person to benefit from these habits. You just need to become a more intentional one.</p>`
      },
      {
        id: 'when-it-gets-real',
        title: 'When It Gets Real: Processing Big Experiences',
        slug: 'when-it-gets-real',
        videoUrl: null,
        readTime: 3,
        keywords: ['processing', 'emotional', 'intense', 'overwhelming', 'crying', 'breakthrough', 'difficult', 'heavy'],
        searchMetadata: {
          aiKeywords: ['processing', 'emotional', 'intense', 'overwhelming', 'breakthrough'],
          intentPhrases: ['how to process a session', 'feeling overwhelmed after session', 'had an intense experience'],
          tags: ['emotions', 'processing'],
        },
        content: `<p>Sometimes a session cracks something open. Maybe a medium connects with someone you've been missing. Maybe an energy healer releases something you didn't know you were carrying. Maybe a coaching session names a pattern you've been avoiding. Maybe a breathwork class unlocks an emotion you didn't expect.</p>

<p>When this happens, it can feel like a lot. Here's how to navigate it.</p>

<p><strong>Let yourself feel it.</strong> Don't rush to intellectualize, explain, or "figure out" what happened. Sit with the emotions. Cry if you need to. Be quiet if you need to. The processing is the work.</p>

<p><strong>Don't make major life decisions immediately.</strong> A powerful session can create a sense of urgency — like you need to change everything right now. Give it a few days. The clarity that's real will still be there next week. The intensity that's just adrenaline will fade.</p>

<p><strong>Talk to someone you trust.</strong> Not everyone in your life will understand what you experienced, and that's OK. Find the one or two people who will listen without judgment — or write it down for yourself.</p>

<p><strong>Reach out to your practitioner if you need to.</strong> Many practitioners welcome brief follow-up messages after an intense session. They've been through this process with many clients and can offer grounding and perspective. Don't hesitate to reach out.</p>

<p><strong>Take care of your body.</strong> Drink water. Get sleep. Eat well. Go outside. Big emotional or physical experiences can be draining. Treat yourself like you're recovering from something — because in a way, you are.</p>

<p>The sessions that shake you are often the ones that change you the most. But change takes time. Trust the process.</p>`
      },
      {
        id: 'combining-practices',
        title: 'Combining Practices for Your Journey',
        slug: 'combining-practices',
        videoUrl: null,
        readTime: 3,
        keywords: ['combine', 'multiple', 'different practices', 'complement', 'variety', 'holistic approach', 'mixing'],
        searchMetadata: {
          aiKeywords: ['combine', 'multiple practices', 'complement', 'holistic'],
          intentPhrases: ['can I try multiple practices', 'combining tarot and coaching', 'holistic approach'],
          tags: ['growth', 'exploration'],
        },
        content: `<p>Here's something no one tells you: you don't have to pick just one.</p>

<p>Many people find that different practices serve different needs — and that combining them creates something richer than any single approach alone.</p>

<p>For example:</p>

<p>A coaching session helps you set clear goals. A yoga practice helps you stay grounded while you pursue them. A tarot reading helps you check in with your deeper intuition about the direction. Together, they address mind, body, and something harder to name.</p>

<p>Or: breathwork gives you the physical experience of release. A coaching call helps you make sense of what came up. Journaling helps you integrate both. Different tools, same journey.</p>

<p>There are no rules about what you can combine. A few natural pairings people often discover:</p>

<p><strong>Coaching + Yoga</strong> — Clarity plus embodiment</p>
<p><strong>Tarot + Coaching</strong> — Insight plus accountability</p>
<p><strong>Energy Healing + Meditation</strong> — Release plus integration</p>
<p><strong>Breathwork + Journaling</strong> — Experience plus reflection</p>
<p><strong>Mediumship + Grief Counseling</strong> — Connection plus processing</p>
<p><strong>Nutrition + Mindfulness</strong> — Body plus awareness</p>

<p>The key is following your own curiosity. If something keeps catching your attention — a practice, a practitioner, a concept — that's information. Follow it.</p>

<p>Your wellness journey doesn't need to look like anyone else's. Build it around what works for <em>you</em>.</p>`
      },
    ],
  },
  {
    id: 'getting-the-most',
    slug: 'getting-the-most',
    title: 'Getting the Most from Your Experience',
    subtitle: 'Simple ways to prepare, be present, and carry the benefits forward.',
    icon: '\u2728',
    color: 'gold',
    articleCount: 2,
    sections: [
      {
        id: 'what-people-mean-by-energy',
        title: "What People Mean When They Talk About 'Energy'",
        slug: 'what-people-mean-by-energy',
        videoUrl: null,
        videoPlaceholder: true,
        readTime: 4,
        keywords: ['energy', 'what is energy', 'vibes', 'aura', 'chakra', 'explain energy', 'woo woo', 'science'],
        searchMetadata: {
          aiKeywords: ['energy', 'vibes', 'aura', 'chakra', 'explain'],
          intentPhrases: ['what do people mean by energy', 'is energy real', 'explain chakras and auras'],
          tags: ['concepts', 'energy'],
        },
        content: `<p>If you're booking a life coach or a yoga teacher, this concept might not come up at all — and that's totally fine. But if you're exploring practices where "energy" is part of the conversation — Reiki, chakra work, sound healing, intuitive readings — this article is for you.</p>

<p>The word "energy" is one of the most overused and under-explained words in the wellness space. Let's make it practical.</p>

<p>You already understand energy intuitively, even if you've never thought of it that way. You've walked into a room after two people had a fight and <em>felt</em> the tension, even though no one said a word. You've met someone and immediately felt at ease — or immediately felt drained. You've had days where everything flows and days where everything feels heavy for no obvious reason.</p>

<p>That's what practitioners mean when they talk about energy. It's the felt quality of a person, a space, or an interaction. It's not magic — it's the part of human experience that exists beyond what we can measure with instruments but that we all recognize when we feel it.</p>

<p>Different traditions have different frameworks for understanding this:</p>

<p><strong>Chakras</strong> (from Hindu and yogic traditions) map energy to specific areas of the body — root, sacral, solar plexus, heart, throat, third eye, crown. When practitioners talk about a "blocked chakra," they mean a pattern of tension, avoidance, or stagnation in that area of your life.</p>

<p><strong>Qi or Chi</strong> (from Chinese medicine) describes the flow of vital energy through the body. Practices like acupuncture and qigong work to keep this flow balanced.</p>

<p><strong>Auras</strong> are described as the energy field surrounding a person. Whether or not you believe in visible auras, most people can relate to the idea of someone having a "heavy" or "light" presence.</p>

<p>You don't need to adopt any of these frameworks. You just need to acknowledge what you already know from lived experience: that some things feel good and some things feel draining, and that paying attention to that feeling is useful information.</p>

<p>That's energy. It's not as woo as it sounds.</p>`
      },
      {
        id: 'before-and-after',
        title: 'Before & After: Getting the Most from Any Session',
        slug: 'before-and-after',
        videoUrl: null,
        readTime: 3,
        keywords: ['grounding', 'protection', 'before session', 'after session', 'self-care', 'boundaries', 'prepare', 'tips'],
        searchMetadata: {
          aiKeywords: ['grounding', 'preparation', 'self-care', 'before session', 'after session'],
          intentPhrases: ['how to prepare for a session', 'what to do after a session', 'getting the most from my session'],
          tags: ['self-care', 'preparation'],
        },
        content: `<p>Whether you're heading into a coaching call, a yoga class, an energy healing, or a reading, there are simple things you can do before and after that make a real difference. None of these are "spiritual rituals" — they're practical habits that help you show up fully and carry the benefits forward.</p>

<h3>Before a session</h3>

<p><strong>Give yourself a buffer.</strong> Don't book a session in between two stressful meetings or right after a fight with your partner. You want to arrive as present and grounded as possible, not carrying the energy of whatever just happened.</p>

<p><strong>Set a simple intention.</strong> It doesn't need to be profound. "I want clarity on this decision" or "I'm here to learn something new" or even "I'm just here to see what happens" — all valid. For a yoga class, it might be "I want to leave feeling calmer than when I arrived."</p>

<p><strong>Take three deep breaths before you start.</strong> Seriously. It shifts your nervous system from "doing" mode to "receiving" mode in about fifteen seconds.</p>

<h3>After a session</h3>

<p><strong>Don't immediately jump back into your to-do list.</strong> Give yourself at least 15-30 minutes of transition time. Walk outside. Sit quietly. Drink water.</p>

<p><strong>Write down what came up while it's fresh.</strong> Your brain will start editing and rationalizing within hours. Capture the raw experience first — what you felt, what surprised you, what you want to remember.</p>

<p><strong>If you feel drained or heavy</strong> (which can happen after deep emotional or energetic work): take a shower, go for a walk, listen to music that lifts you, or simply rest.</p>

<p><strong>If you feel energized and expansive</strong>, enjoy it — but be mindful about making big decisions from that high. Let it integrate first.</p>

<p>These habits work for any kind of session. The more you practice them, the more you'll get from every experience — regardless of the practice.</p>`
      },
    ],
  },
  {
    id: 'becoming-a-practitioner',
    slug: 'becoming-a-practitioner',
    title: 'Thinking About Becoming a Practitioner?',
    subtitle: 'For those feeling the call to do this work.',
    icon: '\u{1F52E}',
    color: 'gold',
    articleCount: 3,
    sections: [
      {
        id: 'signs-you-might-be-called',
        title: "Signs You Might Be Called to This Work",
        slug: 'signs-you-might-be-called',
        videoUrl: null,
        readTime: 3,
        keywords: ['become practitioner', 'calling', 'signs', 'gift', 'purpose', 'start practicing', 'career change'],
        searchMetadata: {
          aiKeywords: ['become practitioner', 'calling', 'signs', 'purpose'],
          intentPhrases: ['should I become a practitioner', 'signs I might be called', 'is this my purpose'],
          tags: ['career', 'calling'],
        },
        content: `<p>This article isn't about convincing you to become a practitioner. It's about naming something you might already be feeling.</p>

<p>A lot of practitioners describe the same pattern: they started as seekers — getting readings, attending workshops, taking classes, exploring practices — and at some point, something shifted. The curiosity became a pull. The interest became a calling.</p>

<p>Some signs that this might be your path:</p>

<p><strong>People already come to you.</strong> Friends call you when they need advice. Strangers open up to you in coffee shops. People tell you "you should do this professionally" and you brush it off, but it keeps happening.</p>

<p><strong>You feel something during sessions that goes beyond being a client.</strong> You find yourself thinking not just about the experience, but about how the practitioner facilitated it. You're curious about the mechanics, the training, the craft behind the work.</p>

<p><strong>You have experiences you can't explain.</strong> Intuitive hits. Knowing things before they happen. Sensing what someone is feeling without being told. Vivid dreams. A connection to something you can't name but can't ignore.</p>

<p><strong>The work gives you energy instead of draining you.</strong> When you think about helping people in this way — whether through healing, teaching, reading, or coaching — it doesn't feel like a job. It feels like purpose.</p>

<p><strong>You keep coming back to it.</strong> You've tried to put it aside and focus on "practical" things, but it keeps surfacing. That persistence is information.</p>

<p>Not everyone who experiences these things is meant to be a practitioner. But if several of these resonate, it might be worth exploring further.</p>`
      },
      {
        id: 'what-the-path-looks-like',
        title: 'What the Path Looks Like',
        slug: 'what-the-path-looks-like',
        videoUrl: null,
        readTime: 4,
        keywords: ['path', 'journey', 'training', 'learning', 'development', 'how to become', 'steps', 'career'],
        searchMetadata: {
          aiKeywords: ['path', 'training', 'learning', 'how to become', 'steps'],
          intentPhrases: ['how to become a practitioner', 'what training do I need', 'steps to become a practitioner'],
          tags: ['career', 'training'],
        },
        content: `<p>There's no single path to becoming a practitioner. Unlike professions with standardized licensing, spiritual and wellness practices have diverse training paths — and that's both the beauty and the challenge of this work.</p>

<p>Here's an honest look at what the journey typically involves:</p>

<p><strong>Study and training.</strong> Most established practitioners have invested in formal or informal training — courses, mentorships, apprenticeships, certifications. The specific training depends on the practice: Reiki has a lineage-based attunement system, yoga requires teacher training certification, coaching has accredited programs, astrology has deep mathematical and interpretive study, mediumship often develops through circles and mentorship. Research what's considered foundational for the practice that calls you.</p>

<p><strong>Personal development.</strong> You can't hold space for other people's growth if you're not doing your own work. Most practitioners will tell you that their own healing, therapy, and personal practice is as important as their professional training.</p>

<p><strong>Practice.</strong> Literally — practicing. Reading for friends, teaching free classes, offering sessions at reduced rates, getting feedback, making mistakes, learning from them. This is where book knowledge becomes embodied skill. It takes time and it takes courage.</p>

<p><strong>Building a business.</strong> This is where many gifted practitioners get stuck. The skills that make someone a powerful healer, teacher, or reader don't automatically translate to knowing how to find clients, set prices, manage bookings, or market themselves. This is exactly what Divine Frameworks was built to help with.</p>

<p><strong>Continuous learning.</strong> The practitioners who do this well never stop learning. They take advanced training, work with mentors, attend workshops, and evolve their practice over years and decades.</p>

<p>The path isn't always linear, and it's rarely fast. But if this is genuinely your calling, the journey itself becomes part of the work.</p>`
      },
      {
        id: 'introductory-courses-coming-soon',
        title: 'Coming Soon: Introductory Courses',
        slug: 'introductory-courses-coming-soon',
        videoUrl: null,
        readTime: 1,
        keywords: ['courses', 'introductory', 'learn', 'training', 'getting started', 'beginner', 'coming soon'],
        searchMetadata: {
          aiKeywords: ['courses', 'training', 'beginner', 'getting started'],
          intentPhrases: ['are there courses available', 'beginner training', 'how to learn more'],
          tags: ['courses', 'future'],
        },
        content: `<p>We're building something for people who are ready to take the next step — structured introductory courses designed to give you a real foundation in the practices you're drawn to.</p>

<p>These won't be fluff. They'll be developed with experienced practitioners and designed to give you both the knowledge and the confidence to start exploring this path seriously.</p>

<p>More details are coming. If you're interested, keep checking back — or follow us on social media for announcements.</p>

<p>In the meantime, the best thing you can do is keep learning, keep experiencing sessions as a seeker, and keep paying attention to what calls to you. The path has a way of unfolding when you're ready.</p>`
      },
    ],
  },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

export function getTopicBySlug(slug) {
  return LEARN_TOPICS.find(t => t.slug === slug) || null;
}
